// utils/adjustingEntryCreateIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /pro/adjusting-entry (manual adjusting journal entry),
// used by pages/ProJournalEntry.tsx -- the only frontend caller of this
// endpoint. Mirrors utils/ownerFundsCreateIdempotency.ts's
// "idle" | "in-flight" | "ambiguous" design exactly (same /pro router, same
// shared backend idempotency foundation).
//
// This module exists because the backend endpoint now REQUIRES an
// idempotency_key (see backend/pro_journal_entry_router.py's
// create_adjusting_entry): a keyless submit is rejected with HTTP 400, so the
// UI must attach a key. The key must be STABLE across an unchanged retry of
// the same logical submission (never regenerated per retry), which is exactly
// what the attempt lifecycle below provides.
//
// The frontend fingerprint here is used ONLY for the frontend's own
// same-vs-edited-retry decision; the AUTHORITATIVE fingerprint is computed
// server-side (_adjusting_fingerprint). They need not be byte-identical.

export type AdjustingEntryLinePayload = {
  account_id: number;
  debit: string;
  credit: string;
  memo: string;
};

export type AdjustingEntryCreatePayload = {
  date: string;
  description: string;
  lines: AdjustingEntryLinePayload[];
};

/**
 * Deterministic canonical payload for POST /pro/adjusting-entry. Mirrors the
 * backend _adjusting_canonical_payload's field selection and normalization:
 *
 * - date / description: passed through (description trimmed, matching the
 *   backend AdjustingJournalEntryIn validator).
 * - lines: kept in the FORM'S OWN ORDER (never re-sorted) -- Nimos does not
 *   treat journal-line order as semantically irrelevant, so reordering lines
 *   is an edit. Each line: account_id -> Number (so "12" and 12 never differ),
 *   debit/credit -> fixed 2-decimal string (matching backend q2 and the
 *   toFixed(2) choice used by canonicalOwnerFundsCreate), memo trimmed
 *   (JournalEntryLine.memo is a real persisted column).
 */
export function canonicalAdjustingEntryCreate(
  date: string,
  description: string,
  lines: Array<{
    account_id: number | string;
    debit: string | number;
    credit: string | number;
    memo: string;
  }>,
): AdjustingEntryCreatePayload {
  return {
    date,
    description: description.trim(),
    lines: lines.map((l) => ({
      account_id: Number(l.account_id),
      debit: Number(l.debit || 0).toFixed(2),
      credit: Number(l.credit || 0).toFixed(2),
      memo: (l.memo || "").trim(),
    })),
  };
}

export function fingerprintAdjustingEntryCreate(
  payload: AdjustingEntryCreatePayload,
): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle (mirrors ownerFundsCreateIdempotency.ts)
// ---------------------------------------------------------------------------

export type AdjustingEntryCreateAttemptState =
  | "idle"
  | "in-flight"
  | "ambiguous";

export type AdjustingEntryCreateAttempt = {
  state: AdjustingEntryCreateAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: AdjustingEntryCreatePayload | null;
};

export const IDLE_ADJUSTING_ENTRY_CREATE_ATTEMPT: AdjustingEntryCreateAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type AdjustingEntryCreateSubmitDecision =
  | {
      kind: "start";
      idempotencyKey: string;
      attempt: AdjustingEntryCreateAttempt;
    }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit may proceed to the network, given the
 * CURRENT attempt and the canonical payload/fingerprint just built from the
 * form. Never mutates `attempt`.
 *
 *  - idle -> fresh attempt, newly generated key.
 *  - ambiguous, SAME fingerprint -> unchanged retry: reuse the stored key
 *    exactly (never mint a new key for an ambiguous outcome).
 *  - ambiguous, DIFFERENT fingerprint -> blocked-mismatch: the prior attempt
 *    under this key may already have committed; an edited resubmission under
 *    the same key must never be sent.
 *  - in-flight -> blocked-in-flight (defensive).
 */
export function decideAdjustingEntryCreateAttempt(
  attempt: AdjustingEntryCreateAttempt,
  payload: AdjustingEntryCreatePayload,
  fingerprint: string,
  generateKey: () => string,
): AdjustingEntryCreateSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or replay). Clears to idle. */
export function resolveAdjustingEntryCreateSuccess(): AdjustingEntryCreateAttempt {
  return IDLE_ADJUSTING_ENTRY_CREATE_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's stack
 * can produce -- 400 (unbalanced, or missing/blank idempotency_key), 403
 * (foreign/missing account), 422 (FastAPI request validation), a closed-period
 * 409, or an idempotency-conflict 409. In every case nothing was persisted
 * under this key (the handler either never ran, or _create_adjusting_impl
 * raised before the claim-insert savepoint completed), so the identity is free
 * to reuse. Returns to idle.
 */
export function resolveAdjustingEntryCreateDefiniteRejection(): AdjustingEntryCreateAttempt {
  return IDLE_ADJUSTING_ENTRY_CREATE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/unreadable response,
 * HTTP 500, or any other unexpected server error where the operation may have
 * committed before the response was constructed (the backend's replay loader
 * _load_adjusting_entry runs AFTER db.commit()). Retains the key/fingerprint/
 * payload so an unchanged retry safely replays instead of risking a duplicate
 * JournalEntry; an edited retry stays blocked.
 */
export function resolveAdjustingEntryCreateAmbiguousFailure(
  attempt: AdjustingEntryCreateAttempt,
): AdjustingEntryCreateAttempt {
  return { ...attempt, state: "ambiguous" };
}

// ---------------------------------------------------------------------------
// Failure classification (mirrors classifyOwnerFundsCreateFailure)
// ---------------------------------------------------------------------------

export type AdjustingEntryCreateFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error. Both a closed-period 409
 * (assert_year_open) and an idempotency-conflict 409
 * (IdempotencyRequestConflict) share HTTP 409, so 409 is disambiguated via the
 * closed-period message matcher; any other 409 is an idempotency conflict.
 * Every other 4xx is a definite pre-commit rejection (nothing persisted).
 * No HTTP status (network failure) or any 5xx is ambiguous -- the replay
 * loader runs after commit, so a 500 can mean the JournalEntry WAS persisted.
 */
export function classifyAdjustingEntryCreateFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): AdjustingEntryCreateFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }

  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt remains
 * unresolved. Conveys: the prior attempt may already have posted; restoring
 * the original values permits a safe retry under the same key; check the
 * General Journal before starting a new entry.
 */
export const ADJUSTING_ENTRY_CREATE_EDITED_RETRY_BLOCKED_MESSAGE =
  "This entry may already be recorded. Retry with the original details, or check the General Journal first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently bound
 * to a different already-completed request. This attempt's data was never
 * persisted under this key.
 */
export const ADJUSTING_ENTRY_CREATE_CONFLICT_MESSAGE =
  "Couldn't confirm this entry. Check the General Journal before trying again.";
